import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bell, CheckCheck, ShoppingBag } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { ConversionTable, TrackLinks, WeeklyEmailPreview } from "@/components/AdminGrowth";

const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];
const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-border bg-card p-4" data-testid={testId}>
    <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="mt-1 font-serif text-3xl">{value}</p>
  </div>
);

const Breakdown = ({ title, rows, testId, unit = "visitors" }) => {
  const max = Math.max(1, ...rows.map((r) => r[unit]));
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testId}>
      <p className="font-mono text-[11px] uppercase tracking-widest text-primary">{title}</p>
      {rows.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No visits yet.</p> : (
        <ul className="mt-3 space-y-2">{rows.map((r) => (
          <li key={r.label} className="text-sm" data-testid={`${testId}-row`}>
            <div className="flex justify-between gap-2"><span className="truncate">{r.label}</span><b>{r[unit]}</b></div>
            <div className="mt-1 h-1.5 rounded-full bg-border"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${(r[unit] / max) * 100}%` }} /></div>
          </li>
        ))}</ul>
      )}
    </div>
  );
};

const DailyChart = ({ series }) => {
  const max = Math.max(1, ...series.map((s) => s.views));
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-4" data-testid="analytics-daily">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[11px] uppercase tracking-widest text-primary">Every day</p>
        <p className="flex gap-3 text-xs text-muted-foreground"><span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-primary/30" />Page views</span><span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-primary" />Visitors</span></p>
      </div>
      <div className="mt-3 flex h-40 items-end gap-[2px]">
        {series.map((s) => (
          <div key={s.day} className="group relative flex h-full flex-1 items-end" title={`${s.day}: ${s.visitors} visitors, ${s.views} page views`}>
            <div className="flex w-full flex-col justify-end overflow-hidden rounded-t bg-primary/30" style={{ height: `${(s.views / max) * 100}%` }}>
              <div className="w-full bg-primary" style={{ height: `${s.views ? (s.visitors / s.views) * 100 : 0}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground"><span>{series[0]?.day}</span><span>{series[series.length - 1]?.day}</span></div>
    </div>
  );
};

export const PurchaseAlerts = ({ alerts, onRead }) => (
  <div className="mt-6 rounded-2xl border border-border bg-card p-4" data-testid="analytics-alerts">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Bell size={13} /> Purchase alerts {alerts.unread > 0 && <span className="rounded-full bg-primary px-2 py-0.5 text-primary-foreground" data-testid="analytics-alerts-unread">{alerts.unread} new</span>}</p>
      {alerts.unread > 0 && <button type="button" onClick={onRead} data-testid="analytics-alerts-read" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><CheckCheck size={13} /> Mark all as seen</button>}
    </div>
    {alerts.items.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No purchases yet. You'll get an email and see them here as they happen.</p> : (
      <ul className="mt-3 divide-y divide-border">{alerts.items.map((a) => (
        <li key={a.id} className={`flex flex-wrap items-start justify-between gap-2 py-2 text-sm ${a.read ? "" : "font-semibold"}`} data-testid="analytics-alert-item">
          <span className="flex items-start gap-2"><ShoppingBag size={15} className="mt-0.5 shrink-0 text-primary" />
            <span>{a.buyer_name} <span className="font-normal text-muted-foreground">({a.buyer_email})</span> bought {a.courses.join(", ")}{typeof a.amount === "number" ? ` · £${a.amount.toFixed(2)}` : ""} · {a.method}</span></span>
          <span className="text-xs font-normal text-muted-foreground">{when(a.created_at)}</span>
        </li>
      ))}</ul>
    )}
  </div>
);

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState({ items: [], unread: 0 });
  const loadAlerts = () => api.get("/admin/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => { setData(null); api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)).catch(() => setData({ error: true })); }, [days]);
  useEffect(() => { loadAlerts(); const t = setInterval(loadAlerts, 60000); return () => clearInterval(t); }, []);
  const markRead = () => api.post("/admin/alerts/read").then(loadAlerts);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-10">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="analytics-back"><ArrowLeft size={14} /> Admin</Link>
        <h1 className="mt-4 font-serif text-4xl">Visitors &amp; purchases</h1>
        <p className="mt-2 text-sm text-muted-foreground">Visitors are counted once per browser per day, with no personal details. Admin pages and search-engine robots aren't counted.</p>
        <PurchaseAlerts alerts={alerts} onRead={markRead} />
        <div className="mt-6 flex flex-wrap gap-2">
          {RANGES.map(([d, label]) => (
            <button key={d} type="button" onClick={() => setDays(d)} data-testid={`analytics-range-${d}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${days === d ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>Last {label}</button>
          ))}
        </div>
        {!data ? <p className="mt-6">Loading…</p> : data.error ? <p className="mt-6 text-red-500">Couldn't load visitor numbers.</p> : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Visitors today" value={data.totals.today_visitors} testId="analytics-today-visitors" />
              <Stat label="Page views today" value={data.totals.today_views} testId="analytics-today-views" />
              <Stat label={`Visitor-days · ${data.days} days`} value={data.totals.visitors} testId="analytics-visitors" />
              <Stat label={`Page views · ${data.days} days`} value={data.totals.views} testId="analytics-views" />
            </div>
            <DailyChart series={data.series} />
            <div className="mt-6 grid gap-3 md:grid-cols-2">
              <Breakdown title="Where they came from" rows={data.source} testId="analytics-source" />
              <Breakdown title="Device" rows={data.device} testId="analytics-device" />
              <Breakdown title="System" rows={data.os} testId="analytics-os" />
              <Breakdown title="Browser" rows={data.browser} testId="analytics-browser" />
              <Breakdown title="Most viewed pages" rows={data.pages} unit="views" testId="analytics-pages" />
            </div>
            <ConversionTable days={data.days} />
          </>
        )}
        <TrackLinks />
        <WeeklyEmailPreview />
      </main>
    </div>
  );
}
